// ============================================
// pages/DispensationPage.jsx - Scriptus Pharma Partner
// Design Glassmorphism consistente com Scriptus RxIA
// ============================================

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { 
  QrCode, 
  Search, 
  CheckCircle, 
  Download, 
  AlertCircle,
  Package,
  User,
  Stethoscope,
  FileText,
  ArrowLeft,
  Loader2,
  Shield,
  Calendar,
  Hash,
  Pill
} from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import HeaderMenu from '../components/HeaderMenu';

// ============================================
// COMPONENTES AUXILIARES
// ============================================

function GlassCard({ children, className = '', elevated = false }) {
  return (
    <div className={`${elevated ? 'glass-card-elevated' : 'glass-card'} rounded-2xl ${className}`}>
      {children}
    </div>
  );
}

function InputField({ label, icon: Icon, error, ...props }) {
  return (
    <div className="space-y-2">
      <label className="block text-sm sf-pro-text font-semibold text-gray-700">{label}</label>
      <div className="relative">
        {Icon && (
          <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        )}
        <input
          className={`input-glass w-full ${Icon ? 'pl-10' : 'pl-4'} pr-4 ${
            error ? 'border-red-400 focus:border-red-500' : ''
          }`}
          {...props}
        />
      </div>
      {error && <p className="text-red-500 text-sm sf-pro-text">{error}</p>}
    </div>
  );
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

export default function DispensationPage() {
  const { token: urlToken } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pharmacy } = useAuth();
  
  // Estados
  const [step, setStep] = useState('search'); // search | review | form | success
  const [token, setToken] = useState(urlToken || searchParams.get('token') || '');
  const [prescription, setPrescription] = useState(null);
  const [dispensedItems, setDispensedItems] = useState([]);
  const [xmlData, setXmlData] = useState(null);
  const [errors, setErrors] = useState({});
  const [scrolled, setScrolled] = useState(false);

  // Detectar scroll para efeito no header
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const isScrolled = window.scrollY > 10;
          if (isScrolled !== scrolled) {
            setScrolled(isScrolled);
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  // Se veio com token na URL, valida automaticamente
  useEffect(() => {
    if (urlToken || searchParams.get('token')) {
      handleValidateToken();
    }
  }, [urlToken]);

  // ============================================
  // MUTATIONS
  // ============================================

  const validateMutation = useMutation({
    mutationFn: (tokenValue) => api.post('/dispensation/validate-token', { token: tokenValue }),
    onSuccess: (response) => {
      const data = response.data;
      
      if (data.status === 'dispensed') {
        toast.error('Esta receita já foi dispensada');
        return;
      }
      
      setPrescription(data);
      
      // Inicializa itens dispensados
      setDispensedItems(data.items.map(item => ({
        ...item,
        ean: '',
        batch: '',
        expirationDate: '',
        quantity: item.quantity
      })));
      
      setStep('review');
      toast.success('Receita validada com sucesso!');
    },
    onError: (error) => {
      const message = error.response?.data?.error?.message || 'Erro ao validar receita';
      toast.error(message);
    }
  });

  const dispenseMutation = useMutation({
    mutationFn: (data) => api.post('/dispensation/dispense', data),
    onSuccess: (response) => {
      setXmlData(response.data.data);
      setStep('success');
      toast.success('Dispensação realizada com sucesso!');
    },
    onError: (error) => {
      const errorData = error.response?.data?.error;
      
      if (errorData?.details) {
        toast.error(
          <div>
            <p className="font-semibold mb-2">Dados incompletos:</p>
            <ul className="text-sm list-disc pl-4">
              {errorData.details.slice(0, 3).map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>,
          { duration: 6000 }
        );
      } else {
        toast.error(errorData?.message || 'Erro ao processar dispensação');
      }
    }
  });

  // ============================================
  // HANDLERS
  // ============================================

  function handleValidateToken() {
    if (!token.trim()) {
      toast.error('Digite ou escaneie o código da receita');
      return;
    }
    validateMutation.mutate(token.trim());
  }

  function handleItemChange(index, field, value) {
    setDispensedItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    
    if (errors[`item_${index}_${field}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`item_${index}_${field}`];
        return newErrors;
      });
    }
  }

  function validateForm() {
    const newErrors = {};
    
    dispensedItems.forEach((item, index) => {
      if (!item.ean) newErrors[`item_${index}_ean`] = 'Código EAN obrigatório';
      if (!item.batch) newErrors[`item_${index}_batch`] = 'Lote obrigatório';
      if (!item.expirationDate) newErrors[`item_${index}_expirationDate`] = 'Validade obrigatória';
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmitDispensation() {
    if (!validateForm()) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    dispenseMutation.mutate({
      prescriptionId: prescription.prescriptionId,
      dispensedItems: dispensedItems.map(item => ({
        ean: item.ean,
        productName: item.name,
        quantity: parseInt(item.quantity),
        batch: item.batch,
        expirationDate: item.expirationDate,
        concentration: item.concentration
      }))
    });
  }

  function handleDownloadXML() {
    if (!xmlData?.sngpcXml) return;
    
    const blob = new Blob([xmlData.sngpcXml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = xmlData.fileName || 'SNGPC_receita.xml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('XML baixado com sucesso!');
  }

  function resetForm() {
    setStep('search');
    setToken('');
    setPrescription(null);
    setDispensedItems([]);
    setXmlData(null);
    setErrors({});
  }

  // ============================================
  // RENDER: Header e Footer comuns
  // ============================================

  const Header = () => (
    <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
      scrolled ? 'glass-header-scrolled' : 'glass-header'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <Link to="/dashboard" className="group transition-all duration-300 hover:opacity-80">
            <img src="/logo.PNG" alt="Scriptus RxIA" className="h-4 sm:h-5 w-auto" />
          </Link>
          <HeaderMenu />
        </div>
      </div>
    </header>
  );

  const Footer = () => (
    <footer className="relative bg-white/80 backdrop-blur-md border-t border-gray-200/50 py-4 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3">
          <img src="/logo.PNG" alt="Scriptus" className="h-3 w-auto opacity-70" />
          <span className="text-xs sf-pro-text text-gray-500">
            Scriptus Tecnologia em Saúde LTDA © 62.420.408/0001-70
          </span>
        </div>
      </div>
    </footer>
  );

  // ============================================
  // RENDER: Step 1 - Buscar/Escanear
  // ============================================

  if (step === 'search') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 relative flex flex-col">
        {/* Background Pattern */}
        <div className="fixed inset-0 z-0 pointer-events-none opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              radial-gradient(circle at 25% 25%, rgb(16 185 129 / 0.08) 0%, transparent 50%),
              radial-gradient(circle at 75% 75%, rgb(20 184 166 / 0.05) 0%, transparent 50%)
            `
          }}></div>
        </div>

        <Header />

        <main className="flex-1 max-w-xl mx-auto px-4 sm:px-6 pt-20 sm:pt-24 pb-12 relative z-10 w-full">
          {/* Botão Voltar */}
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 transition sf-pro-text font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Voltar ao Dashboard</span>
          </button>

          {/* Título */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl sf-pro-display font-semibold text-gray-900 tracking-tight mb-2">
              Validar Receita
            </h1>
            <p className="sf-pro-text text-gray-500 font-medium">
              Escaneie o QR Code ou digite o código da receita
            </p>
          </div>
          
          <GlassCard elevated className="p-6 sm:p-8">
            <div className="flex items-center justify-center mb-8">
              <div className="icon-container p-4 rounded-2xl bg-emerald-100/80 border-emerald-200/50">
                <QrCode className="w-10 h-10 text-emerald-600" />
              </div>
            </div>
            
            <div className="space-y-6">
              <InputField
                label="Código da Receita"
                icon={Search}
                placeholder="Digite o token da receita..."
                value={token}
                onChange={(e) => setToken(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleValidateToken()}
              />
              
              <button
                onClick={handleValidateToken}
                disabled={validateMutation.isPending}
                className="button-primary w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
              >
                {validateMutation.isPending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="sf-pro-text font-semibold">Validando...</span>
                  </>
                ) : (
                  <>
                    <Shield className="w-5 h-5" />
                    <span className="sf-pro-text font-semibold">Validar Receita</span>
                  </>
                )}
              </button>
              
              <div className="text-center">
                <button className="text-gray-400 text-sm hover:text-gray-600 transition sf-pro-text">
                  Ou escaneie o QR Code com a câmera
                </button>
              </div>
            </div>

            {/* Info de Segurança */}
            <div className="mt-8 pt-6 border-t border-gray-200/50">
              <div className="flex items-start gap-3">
                <div className="p-1.5 rounded-full bg-emerald-100/80 mt-0.5">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="sf-pro-text font-semibold text-gray-900 text-sm">Validação ICP-Brasil</p>
                  <p className="sf-pro-text text-gray-500 text-xs">Todas as receitas são verificadas automaticamente</p>
                </div>
              </div>
            </div>
          </GlassCard>
        </main>

        <Footer />

        <style>{styles}</style>
      </div>
    );
  }

  // ============================================
  // RENDER: Step 2 - Revisar Receita
  // ============================================

  if (step === 'review') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 relative flex flex-col">
        <div className="fixed inset-0 z-0 pointer-events-none opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              radial-gradient(circle at 25% 25%, rgb(16 185 129 / 0.08) 0%, transparent 50%),
              radial-gradient(circle at 75% 75%, rgb(20 184 166 / 0.05) 0%, transparent 50%)
            `
          }}></div>
        </div>

        <Header />

        <main className="flex-1 max-w-2xl mx-auto px-4 sm:px-6 pt-20 sm:pt-24 pb-12 relative z-10 w-full">
          <button
            onClick={() => setStep('search')}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 transition sf-pro-text font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Voltar</span>
          </button>

          {/* Título */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl sf-pro-display font-semibold text-gray-900 tracking-tight mb-2">
              Receita Validada
            </h1>
            <p className="sf-pro-text text-gray-500 font-medium">
              Confira os dados antes de prosseguir
            </p>
          </div>
          
          <GlassCard elevated className="p-6 sm:p-8">
            {/* Status de validação */}
            <div className="flex items-center gap-3 p-4 bg-emerald-50/80 border border-emerald-200/50 rounded-xl mb-6">
              <div className="p-2 rounded-full bg-emerald-100">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="sf-pro-text font-semibold text-emerald-700">Assinatura Verificada</p>
                <p className="sf-pro-text text-emerald-600 text-sm">Certificado digital ICP-Brasil válido</p>
              </div>
            </div>
            
            {/* Dados do Médico */}
            <div className="mb-6">
              <div className="flex items-center gap-2 text-gray-700 mb-3">
                <Stethoscope className="w-5 h-5 text-gray-500" />
                <span className="sf-pro-text font-semibold">Prescritor</span>
              </div>
              <div className="bg-gray-50/80 border border-gray-200/50 rounded-xl p-4">
                <p className="sf-pro-text font-semibold text-gray-900">{prescription.doctor?.name}</p>
                <p className="sf-pro-text text-gray-500 text-sm">
                  CRM {prescription.doctor?.crm} - {prescription.doctor?.ufCrm}
                </p>
              </div>
            </div>
            
            {/* Dados do Paciente */}
            <div className="mb-6">
              <div className="flex items-center gap-2 text-gray-700 mb-3">
                <User className="w-5 h-5 text-gray-500" />
                <span className="sf-pro-text font-semibold">Paciente</span>
              </div>
              <div className="bg-gray-50/80 border border-gray-200/50 rounded-xl p-4">
                <p className="sf-pro-text font-semibold text-gray-900">{prescription.patient?.name}</p>
                {prescription.patient?.documentMasked && (
                  <p className="sf-pro-text text-gray-500 text-sm">CPF: {prescription.patient.documentMasked}</p>
                )}
              </div>
            </div>
            
            {/* Medicamentos */}
            <div className="mb-8">
              <div className="flex items-center gap-2 text-gray-700 mb-3">
                <Package className="w-5 h-5 text-gray-500" />
                <span className="sf-pro-text font-semibold">Medicamentos Prescritos</span>
              </div>
              <div className="space-y-3">
                {prescription.items?.map((item, index) => (
                  <div key={index} className="bg-gray-50/80 border border-gray-200/50 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-blue-100/80 border border-blue-200/50">
                        <Pill className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="sf-pro-text font-semibold text-gray-900">{item.name}</p>
                        <div className="flex flex-wrap gap-3 mt-1 text-sm sf-pro-text text-gray-500">
                          <span>Qtd: {item.quantity}</span>
                          {item.concentration && <span>{item.concentration}</span>}
                        </div>
                        {item.dosage && (
                          <p className="sf-pro-text text-gray-400 text-sm mt-2 italic">{item.dosage}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Ação */}
            <button
              onClick={() => setStep('form')}
              className="button-primary w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
            >
              <FileText className="w-5 h-5" />
              <span className="sf-pro-text font-semibold">Preencher Dados SNGPC</span>
            </button>
          </GlassCard>
        </main>

        <Footer />

        <style>{styles}</style>
      </div>
    );
  }

  // ============================================
  // RENDER: Step 3 - Formulário SNGPC
  // ============================================

  if (step === 'form') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 relative flex flex-col">
        <div className="fixed inset-0 z-0 pointer-events-none opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              radial-gradient(circle at 25% 25%, rgb(16 185 129 / 0.08) 0%, transparent 50%),
              radial-gradient(circle at 75% 75%, rgb(20 184 166 / 0.05) 0%, transparent 50%)
            `
          }}></div>
        </div>

        <Header />

        <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 pt-20 sm:pt-24 pb-12 relative z-10 w-full">
          <button
            onClick={() => setStep('review')}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 transition sf-pro-text font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Voltar</span>
          </button>

          {/* Título */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl sf-pro-display font-semibold text-gray-900 tracking-tight mb-2">
              Dados para SNGPC
            </h1>
            <p className="sf-pro-text text-gray-500 font-medium">
              Preencha o lote e validade de cada medicamento
            </p>
          </div>
          
          <GlassCard elevated className="p-6 sm:p-8">
            <div className="space-y-6">
              {dispensedItems.map((item, index) => (
                <div key={index} className="bg-gray-50/80 border border-gray-200/50 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-blue-100/80 border border-blue-200/50">
                      <Pill className="w-4 h-4 text-blue-600" />
                    </div>
                    <h3 className="sf-pro-text font-semibold text-gray-900">{item.name}</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <InputField
                      label="Código EAN *"
                      icon={Hash}
                      placeholder="7891234567890"
                      value={item.ean}
                      onChange={(e) => handleItemChange(index, 'ean', e.target.value)}
                      error={errors[`item_${index}_ean`]}
                    />
                    
                    <InputField
                      label="Lote *"
                      icon={Package}
                      placeholder="L123456"
                      value={item.batch}
                      onChange={(e) => handleItemChange(index, 'batch', e.target.value)}
                      error={errors[`item_${index}_batch`]}
                    />
                    
                    <InputField
                      label="Validade *"
                      icon={Calendar}
                      type="date"
                      value={item.expirationDate}
                      onChange={(e) => handleItemChange(index, 'expirationDate', e.target.value)}
                      error={errors[`item_${index}_expirationDate`]}
                    />
                  </div>
                  
                  <div className="mt-4">
                    <InputField
                      label="Quantidade dispensada"
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
            
            <button
              onClick={handleSubmitDispensation}
              disabled={dispenseMutation.isPending}
              className="button-primary w-full mt-8 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
            >
              {dispenseMutation.isPending ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="sf-pro-text font-semibold">Processando...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  <span className="sf-pro-text font-semibold">Confirmar Dispensação</span>
                </>
              )}
            </button>
          </GlassCard>
        </main>

        <Footer />

        <style>{styles}</style>
      </div>
    );
  }

  // ============================================
  // RENDER: Step 4 - Sucesso
  // ============================================

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 relative flex flex-col">
        <div className="fixed inset-0 z-0 pointer-events-none opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              radial-gradient(circle at 25% 25%, rgb(16 185 129 / 0.08) 0%, transparent 50%),
              radial-gradient(circle at 75% 75%, rgb(20 184 166 / 0.05) 0%, transparent 50%)
            `
          }}></div>
        </div>

        <Header />

        <main className="flex-1 flex items-center justify-center px-4 sm:px-6 pt-20 sm:pt-24 pb-12 relative z-10">
          <GlassCard elevated className="p-8 max-w-md w-full text-center">
            <div className="w-20 h-20 bg-emerald-100/80 border border-emerald-200/50 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            
            <h1 className="text-2xl sf-pro-display font-semibold text-gray-900 mb-2">
              Dispensação Concluída
            </h1>
            <p className="sf-pro-text text-gray-500 mb-8">
              O XML SNGPC foi gerado com sucesso. Baixe o arquivo para enviar ao portal da ANVISA.
            </p>
            
            <button
              onClick={handleDownloadXML}
              className="button-primary w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white mb-4"
            >
              <Download className="w-5 h-5" />
              <span className="sf-pro-text font-semibold">Baixar XML SNGPC</span>
            </button>
            
            <button
              onClick={resetForm}
              className="button-glass w-full text-gray-700 hover:text-gray-900"
            >
              <QrCode className="w-4 h-4" />
              <span className="sf-pro-text font-medium">Nova Dispensação</span>
            </button>
            
            <button
              onClick={() => navigate('/dashboard')}
              className="mt-6 text-gray-400 hover:text-gray-600 transition text-sm sf-pro-text font-medium"
            >
              Voltar ao Dashboard
            </button>
          </GlassCard>
        </main>

        <Footer />

        <style>{styles}</style>
      </div>
    );
  }

  return null;
}

// ============================================
// ESTILOS CSS
// ============================================

const styles = `
  .sf-pro-display {
    font-family: -apple-system, "SF Pro Display", BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
    font-feature-settings: "liga" 1, "kern" 1;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  
  .sf-pro-text {
    font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
    font-feature-settings: "liga" 1, "kern" 1;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  .glass-card {
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 
      0 4px 16px rgba(0, 0, 0, 0.08),
      inset 0 1px 0 rgba(255, 255, 255, 0.6);
  }

  .glass-card-elevated {
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    border: 1px solid rgba(255, 255, 255, 0.4);
    box-shadow: 
      0 8px 32px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.7),
      inset 0 -1px 0 rgba(255, 255, 255, 0.3);
  }

  .icon-container {
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 
      0 2px 6px rgba(0, 0, 0, 0.05),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
  }

  .glass-header {
    background: rgba(255, 255, 255, 0.5);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: 
      0 1px 3px rgba(0, 0, 0, 0.05),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }

  .glass-header-scrolled {
    background: rgba(255, 255, 255, 0.75);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.4);
    box-shadow: 
      0 2px 12px rgba(0, 0, 0, 0.1),
      0 8px 32px rgba(0, 0, 0, 0.05),
      inset 0 1px 0 rgba(255, 255, 255, 0.6);
  }

  .input-glass {
    padding: 12px 16px;
    background: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(229, 231, 235, 0.8);
    border-radius: 12px;
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, sans-serif;
    font-size: 14px;
    color: #1f2937;
    box-shadow: 
      0 2px 8px rgba(0, 0, 0, 0.04),
      inset 0 1px 0 rgba(255, 255, 255, 0.7);
  }

  .input-glass::placeholder {
    color: #9ca3af;
  }

  .input-glass:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(16, 185, 129, 0.5);
    box-shadow: 
      0 0 0 3px rgba(16, 185, 129, 0.1),
      0 6px 20px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }

  .button-primary {
    padding: 14px 20px;
    border-radius: 12px;
    font-weight: 600;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    box-shadow: 
      0 6px 20px rgba(16, 185, 129, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.25);
  }

  .button-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 
      0 10px 32px rgba(16, 185, 129, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }

  .button-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  .button-glass {
    padding: 12px 20px;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid rgba(229, 231, 235, 0.8);
    border-radius: 12px;
    font-weight: 500;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    box-shadow: 
      0 2px 8px rgba(0, 0, 0, 0.04),
      inset 0 1px 0 rgba(255, 255, 255, 0.6);
  }

  .button-glass:hover {
    background: rgba(255, 255, 255, 0.95);
    transform: translateY(-1px);
    box-shadow: 
      0 4px 16px rgba(0, 0, 0, 0.08),
      inset 0 1px 0 rgba(255, 255, 255, 0.8);
  }

  html {
    scroll-behavior: smooth;
  }

  @media (max-width: 768px) {
    .glass-card-elevated,
    .glass-card,
    .glass-header,
    .glass-header-scrolled {
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
    }
    
    .input-glass {
      font-size: 16px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    * {
      animation-duration: 0.01ms !important;
      transition-duration: 0.01ms !important;
    }
  }

  @media (prefers-reduced-transparency: reduce) {
    .glass-card-elevated,
    .glass-card,
    .glass-header,
    .glass-header-scrolled {
      background: rgba(255, 255, 255, 0.98);
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }
  }
`;